import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Row, Col, Pagination, Spinner } from "react-bootstrap";
import BTable from "react-bootstrap/Table";
import { useTable, useSortBy } from "react-table";
import { useNavigate } from "react-router-dom";
import { left, right } from "../../assets/img";
import { MaskIcon } from "../MaskIcon";
import { PRIMARY_COLOR } from "../../Services/Colors";

// Memoized Table Row to prevent unnecessary re-renders
const TableRow = React.memo(
  ({ row, color, onCellClick }) => {
    return (
      <tr className="text-start f2" {...row.getRowProps()}>
        {row.cells.map((cell, ind) => {
          const isClickable = [
            "id",
            "client_id",
            "name",
            "mobile",
            "related_client_mobile",
            "client_mobile",
          ].includes(cell.column.id);

          return (
            <td
              key={ind}
              style={{
                borderTop: "1px solid #bcbcbc",
                borderLeft: "none",
                color: color,
                cursor: isClickable ? "pointer" : "default",
                verticalAlign: "middle",
              }}
              className="f3 fs-xs-14 fs-md-14 gray2 list-data"
              {...cell.getCellProps()}
              onClick={() => {
                if (cell.column.Header === " To Phone No")
                  onCellClick("toPhone", row);
                else if (isClickable) onCellClick("other", row);
              }}
            >
              {cell.render("Cell")}
            </td>
          );
        })}
      </tr>
    );
  },
);

export function OptimizedTable({
  columns,
  data = [],
  loading = false,
  setPageno,
  pagecount = 0,
  totalPages,
  show,
  tableHeight = 400,
  tableMargin,
  pageno,
  color = "#000",
}) {
  const navigate = useNavigate();
  const [pagenumber, setPagenumber] = useState(1);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    state: { pageIndex },
  } = useTable(
    {
      columns,
      data,
      manualPagination: true, // external pagination
      pageCount: totalPages,
      initialState: { pageIndex: pageno ? pageno - 1 : 0 },
    },
    useSortBy,
  );

  useEffect(() => {
    setPagenumber(pageIndex + 1);
  }, [pageIndex]);

  const handleCellClick = useCallback(
    (type, row) => {
      if (type === "toPhone") {
        navigate("/client-report", {
          state: { type: "edit", data: { id: row.original?.related_client_id } },
        });
      } else {
        navigate("/client-report", { state: { type: "edit", data: row.original } });
      }
    },
    [navigate],
  );

  return (
    <div
      className="table-design tablealign position-relative"
      style={{
        marginBottom: tableMargin ? tableMargin : 130,
        // paddingBottom: 30,
        // height: tableHeight,
        overflowY: "auto",
      }}
    >
      <BTable
        size="sm"
        responsive
        {...getTableProps()}
        className="table-border table mb-0"
        style={{ border: "1px solid #bcbcbc", borderCollapse: "separate", borderSpacing: 0 }}
      >
        <thead className="fixed-header">
          {headerGroups.map((headerGroup, ind) => (
            <tr className="thfiled" {...headerGroup.getHeaderGroupProps()} key={ind}>
              {headerGroup.headers.map((column, indx) => (
                <th
                  className="px-2 text-center"
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  key={indx}
                  style={{
                    verticalAlign: "middle",
                    borderBottom: "1.5px solid #bcbcbc",
                    borderRight: "2px solid #d9d9d9",
                    backgroundClip: "padding-box",
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                  }}
                >
                  <h4 style={{ fontSize: 15, color: PRIMARY_COLOR }} className="f3 fs-xs-14 fs-md-15 py-2 word-per-line">
                    {column.render("Header")}
                  </h4>
                  <span>
                    {column.isSorted ? (
                      column.isSortedDesc ? (
                        <span className="feather icon-arrow-down text-muted float-right" />
                      ) : (
                        <span className="feather icon-arrow-up text-muted float-right" />
                      )
                    ) : (
                      ""
                    )}
                  </span>
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody {...getTableBodyProps()}>
          {loading ? (
            <tr style={{ backgroundColor: "#f0ede6" }}>
              <td
                style={{ backgroundColor: "#f0ede6" }}
                colSpan={columns.length}
                className="text-center py-5"
              >
                <Spinner animation="border" size="sm" /> &nbsp; Loading data...
              </td>
            </tr>
          ) : rows.length > 0 ? (
            rows.map((row) => {
              prepareRow(row);
              return (
                <TableRow
                  key={row.id}
                  row={row}
                  color={color}
                  onCellClick={handleCellClick}
                />
              );
            })
          ) : (
            <tr style={{ backgroundColor: "#f0ede6" }}>
              <td
                colSpan={columns.length}
                className="text-center py-5 text-muted"
              >
                No data found
              </td>
            </tr>
          )}
        </tbody>
      </BTable>

      {/* Pagination */}
      {show && (
        <Row className="justify-content-between table-show p-1 w-100 bg-primary2">
          <Col className="d-flex ac-jb pagination_box">
            <p className="px-1 mb-0 f3 fs-xs-13 fs-md-14">
              Total Pages: <strong>{pagecount}</strong>
            </p>
            <Pagination className="gap-1 pointer mb-0 d-flex ac-js align-items-center">
              {pageno > 1 && (
                <MaskIcon
                  src={left}
                  width={18}
                  alt="Previous"
                  onClick={() => pageno > 1 && setPageno(pageno - 1)}
                  style={{ cursor: "pointer" }}
                />
              )}
              <input
                type="number"
                value={pageno === "" ? "" : pageno}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "") {
                    setPageno("");
                    return;
                  }
                  const num = Number(value);
                  if (num >= 1 && num <= pagecount) setPageno(num);
                }}
                onBlur={() => {
                  if (pageno === "") setPageno(1);
                }}
                style={{
                  width: "28px",
                  height: "26px",
                  borderRadius: "8px",
                  border: `2px solid ${PRIMARY_COLOR}`,
                }}
                className="form-control text-center p-0 placholdercolorchanges2"
              />
              {pageno < pagecount && (
                <img
                  src={right}
                  width={18}
                  alt="Next"
                  onClick={() => pageno < pagecount && setPageno(pageno + 1)}
                  style={{ cursor: "pointer" }}
                />
              )}
            </Pagination>
          </Col>
        </Row>
      )}
    </div>
  );
}
